/**
 * RuntimeEnvironmentInfoUtil - Provide Runtime Environment Information.
 * Mainly to be used by Microfrontend which needs to defect if running inside WebView or not.
 */

/**
 * Common groupings of OS and View
 */
export enum RuntimeEnvironment {
  androidWebview = 'androidWebview',
  iosWebview = 'iosWebview',
  browser = 'browser',
}

export enum OS {
  android = 'android',
  ios = 'ios',
  linux = 'linux',
  macos = 'macos',
  undetermined = 'undetermined',
  windows = 'windows',
}

export enum View {
  browser = 'browser',
  webview = 'webview',
}

export interface IRuntimeEnvironmentInfo {
  runtimeEnvironment: RuntimeEnvironment;
  /** if running inside Android Dayforce app's WebView */
  isAndroidWebview: boolean;
  /** if running in desktop/laptop browsers, iOS Safari, iPadOS Safari, and Android Chrome */
  isBrowser: boolean;
  /** if running inside iOS Dayforce app's WebView */
  isIOSWebview: boolean;
  os: OS,
  view: View,
}


const isValidOs = (maybeOs: OS | string): maybeOs is OS => {
  return maybeOs in OS
}

const isValidView = (maybeView: View | string): maybeView is View => {
  return maybeView in View
}
/**
 * @internal
 */
export const determineOs = (userAgent: string, override: string): OS => {

  if (isValidOs(override)) {
    return override;
  }

  /**
   * The ordering of these checks matters, making it tricky to change.
   * For example, many iPhone/iPad UAs also contain "Mac OS",
   * and Android UAs include "Linux", so check for mobile devices first
   */
  if (/iphone|ipod|ipad/i.test(userAgent)) {
    return OS.ios;
  } else if (
    /Mac OS/i.test(userAgent) &&
    /** 
     * Some newer iPads use the Safari desktop User Agent string.
     * These extra checks distinguish between iPads and MacOS browsers.
     * Checks that `document` and `window` exist prevent crashes when this function runs inside a web worker.
     */
    typeof document !== 'undefined' &&
    typeof window !== 'undefined' &&
    "ontouchend" in document &&
    !(window as any).MSStream) {
    return OS.ios;
  } else if (/android/i.test(userAgent)) {
    return OS.android;
  } else if (/Mac OS/i.test(userAgent)) {
    return OS.macos;
  } else if (/Windows/i.test(userAgent)) {
    return OS.windows;
  } else if (/Linux/i.test(userAgent)) {
    return OS.linux;
  }

  return OS.undetermined;
}

const determineView = (pathname: string, override: string): View => {

  if (isValidView(override)) {
    return override;
  }

  return pathname.match(/u\/([^\/]{22}|0)\/MobileView/)
    ? View.webview : View.browser
}

const determineEnvType = ({ os, view }: { os: OS, view: View }): RuntimeEnvironment => {

  const mappings = {
    [OS.android]: {
      [View.webview]: RuntimeEnvironment.androidWebview,
      [View.browser]: RuntimeEnvironment.browser
    },
    [OS.ios]: {
      [View.webview]: RuntimeEnvironment.iosWebview,
      [View.browser]: RuntimeEnvironment.browser
    },
  }

  if (mappings[os]) {
    return mappings[os][view]
  }

  return RuntimeEnvironment.browser;
}

/**
 * Get information about the environment the web application is running in.
 *
 * 
 * @remarks
 * This may return incorrect values for some iPads when run inside a web worker since some checks can't run.
 * 
 * This can be run at the module level when a web application starts up.
 * It does not need to be run on every component re-render.
 * 
 * @example with React
 * ```jsx
 * import React from' react
 * import { getRuntimeEnvironmentInfo, View } from '@platform/core'
 * 
 * // prefer running this at the module level so it executes once, not inside a React component
 * const { view } = getRuntimeEnvironmentInfo();
 * 
 * const SomeComponent = () => {
 * 
 *   if (view === View.webview) {
 *    // do something iOS specific in here
 *   }
 * 
 * return <div ...
 * ```
 * 
 * If you need to override the view or OS during development to reach conditional code, 
 * use URL parameters like this in your browser:
 * 
 * @example using URL parameters to force this function to return that it is running in an iOS webview
 * ```text
 * http://localhost:8003?env.os=ios&env.view=webview
 * ```
 * 
 * @returns information about the environment JS is running in
 */
export const getRuntimeEnvironmentInfo = (): IRuntimeEnvironmentInfo => {

  const userAgent = self.navigator.userAgent.toLowerCase();
  const pathName = self.location.pathname;
  const urlSearchParams = new URLSearchParams(self.location.search)

  const os = determineOs(userAgent, urlSearchParams.get('env.os'))
  const view = determineView(pathName, urlSearchParams.get('env.view'))
  const runtimeEnvironment = determineEnvType({ os, view });

  return {
    isAndroidWebview: runtimeEnvironment === RuntimeEnvironment.androidWebview,
    isBrowser: runtimeEnvironment === RuntimeEnvironment.browser,
    isIOSWebview: runtimeEnvironment === RuntimeEnvironment.iosWebview,
    os,
    runtimeEnvironment,
    view,
  };
}